import styled, { css, keyframes } from 'styled-components'
import { theme } from '@src/containers/WordStat/common'

export type StyledSelectProps = {
  isSelected: boolean
  isOpen: boolean
  disabled?: boolean
}

export const Input = styled.div<StyledSelectProps>`
  padding: 15px 20px;
  height: 50px;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: space-between;

  background-color: ${({ theme }) => theme.palette.white};
  border: 1px solid ${({ theme }) => theme.palette.black[80]};
  border-radius: 5px;

  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family: Navigo, Arial, Helvetica, sans-serif;

  &:hover {
    outline: 1px solid ${({ theme }) => theme.palette.black[80]};
  }

  & svg {
    color: ${({ theme }) => theme.palette.black[20]};
    transition 200ms ease-in-out 0s;
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      & > svg {
        transform: rotate(180deg);
      }
    `}

  ${({ isSelected, theme }) =>
    isSelected
      ? css`
          color: ${theme.palette.black[20]};
        `
      : css`
          color: ${theme.palette.black[70]};
        `}

  ${({ disabled, theme }) =>
    disabled &&
    css`
      color: ${theme.palette.black[70]};
      background-color: #edeff3;
      cursor: initial;

      & > svg {
        color: ${theme.palette.black[70]};
      }
    `}
`

const animationAppearance = keyframes`
  from {
    opacity: 0;
  } to {
    opacity: 1;
  }
`

export const Year = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;

  @media (max-width: 767px) {
    row-gap: 10px;
  }
`

type StyledContainerProps = {
  isOpen: boolean
  isPaginationAvailable: boolean
}

export const Container = styled.div<StyledContainerProps>`
  position: relative;
  padding: 20px;
  display: flex;
  column-gap: 30px;
  border-radius: 5px;

  background-color: ${({ theme }) => theme.palette.white};
  box-shadow: 0px 4px 20px 0px rgba(82, 92, 122, 0.15);
  animation: ${animationAppearance} 200ms linear forwards;
  z-index: 2;

  @media (max-width: 767px) {
    padding: 20px 10px 10px;
    flex-direction: column;
    row-gap: 30px;
  }

  ${({ isOpen }) => css`
    display: ${isOpen ? 'flex' : 'none'};
  `}

  ${({ isPaginationAvailable }) =>
    isPaginationAvailable &&
    css`
      ${Year} {
        padding-top: 10px;
        row-gap: 30px;

        @media (max-width: 767px) {
          row-gap: 10px;
          padding-top: initial;
        }
      }
    `}
`

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`

export const Body = styled.div`
  display: grid;
  grid-template-rows: repeat(4, 40px);
  grid-template-columns: repeat(3, 100px);
  gap: 5px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(3, minmax(86px, 100px));
  }
`

export enum MonthBorder {
  BOTH,
  LEFT,
  RIGHT
}

export type StyledMonthProps = {
  isSelected?: boolean
  isInBetween?: boolean
  isHovered?: boolean
  monthBorder?: MonthBorder
}

export const Month = styled.button<StyledMonthProps>`
  --background-color: transparent;
  --background-color-disabled: ${({ theme }) => theme.palette.white};
  --background-color-hover: #edeff3;

  --color: ${({ theme }) => theme.palette.black[20]};
  --color-disabled: ${({ theme }) => theme.palette.black[70]};

  --border-color: #edeff3;

  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  color: var(--color);
  background-color: var(--background-color);

  border-radius: 3px;
  border: 1px solid var(--border-color);

  &:hover:not(:disabled) {
    background-color: var(--background-color-hover);
  }

  &:disabled {
    cursor: initial;
    background-color: var(--background-color-disabled);
    color: var(--color-disabled);
    border: none;
  }

  ${({ isHovered, disabled }) =>
    isHovered &&
    !disabled &&
    css`
      background-color: var(--background-color-hover);

      &:nth-child(3n + 1) {
        box-shadow: 5px 0 var(--background-color-hover);
      }

      &:nth-child(3n + 2) {
        box-shadow: -5px 0 var(--background-color-hover), 5px 0 var(--background-color-hover);
      }

      &:nth-child(3n) {
        box-shadow: -5px 0 var(--background-color-hover);
      }
    `}

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      --color: ${theme.palette.white};
      --background-color: ${theme.palette.blue[60]};
      --background-color-hover: #1245f7;
      border: none;
      z-index: 1;

      &:nth-child(3n + 1) {
        box-shadow: 2.9px 0 var(--background-color);
      }

      &:nth-child(3n + 2) {
        box-shadow: -2.9px 0 var(--background-color), 2.9px 0 var(--background-color);
      }

      &:nth-child(3n) {
        box-shadow: -2.9px 0 var(--background-color);
      }
    `}

  ${({ isInBetween, theme }) =>
    isInBetween &&
    css`
      --background-color: ${theme.palette.blue[90]};
      --background-color-hover: ${theme.palette.blue[90]};
      border: none;
      border-radius: none;

      &:nth-child(3n + 1) {
        box-shadow: 5px 0 var(--background-color);
      }

      &:nth-child(3n + 2) {
        box-shadow: -5px 0 var(--background-color), 5px 0 var(--background-color);
      }

      &:nth-child(3n) {
        box-shadow: -5px 0 var(--background-color);
      }
    `}

    ${({ monthBorder }) => {
      switch (monthBorder) {
        case MonthBorder.LEFT:
          return css`
            &:not(:nth-child(3n)) {
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
            }
          `
        case MonthBorder.RIGHT:
          return css`
            &:not(:nth-child(3n + 1)) {
              border-top-left-radius: 0;
              border-bottom-left-radius: 0;
            }
          `
        case MonthBorder.BOTH:
        default:
          break
      }
    }}
`

export const PaginationContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  display: flex;
  justify-content: space-between;

  @media (max-width: 767px) {
    top: 10px;
    left: 10px;
    right: 10px;
  }
`

export const PaginationButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${theme.palette.black(94)};
  color: ${theme.palette.black(20)};
  border-radius: 3px;

  &:hover:not(:disabled) {
    background-color: ${theme.palette.blue(94)};
  }

  &:disabled {
    cursor: initial;
    background-color: ${theme.palette.black(94)};
    color: ${theme.palette.black(60)};
  }

  @media (max-width: 767px) {
    width: 30px;
    height: 30px;
  }
`
